import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Clock, 
  Calendar,
  MessageSquare,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function CallHistory() {
  const { getAuthHeader } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCall, setExpandedCall] = useState(null);

  useEffect(() => {
    fetchCalls();
  }, []);

  const fetchCalls = async () => {
    try {
      const res = await axios.get(`${API}/calls?limit=100`, { headers: getAuthHeader() });
      setCalls(res.data);
    } catch (error) {
      toast.error("Failed to load call history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Group calls by date
  const groupedCalls = calls.reduce((groups, call) => {
    const date = formatDate(call.started_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(call);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="history-loading">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full"></span>
          <span className="w-2 h-2 bg-primary rounded-full"></span>
          <span className="w-2 h-2 bg-primary rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="call-history-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Call History</h1>
        <p className="text-muted-foreground">View your past conversations and transcripts</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Phone className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Calls</p>
              <p className="text-2xl font-bold">{calls.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Duration</p>
              <p className="text-2xl font-bold">
                {formatDuration(calls.reduce((acc, call) => acc + call.duration_seconds, 0))}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Messages</p>
              <p className="text-2xl font-bold">
                {calls.reduce((acc, call) => acc + (call.transcript?.length || 0), 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calls List */}
      <Card className="bg-card border-border" data-testid="calls-list">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Recent Calls
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(groupedCalls).length === 0 ? (
            <div className="text-center py-12" data-testid="no-calls">
              <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading font-semibold mb-2">No calls yet</h3>
              <p className="text-muted-foreground">
                Start a voice call to see your history here
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedCalls).map(([date, dateCalls]) => (
                <div key={date}>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {date}
                  </h3>
                  <div className="space-y-3">
                    {dateCalls.map((call) => (
                      <Collapsible 
                        key={call.id}
                        open={expandedCall === call.id}
                        onOpenChange={(open) => setExpandedCall(open ? call.id : null)}
                      >
                        <div 
                          className="bg-secondary/30 rounded-xl overflow-hidden"
                          data-testid={`call-item-${call.id}`}
                        >
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              className="w-full p-4 h-auto justify-between hover:bg-secondary/50"
                              data-testid={`expand-call-${call.id}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={cn(
                                  "w-10 h-10 rounded-full flex items-center justify-center",
                                  call.status === 'active' ? 'bg-emerald-500/20' : 'bg-muted'
                                )}>
                                  <Phone className={cn(
                                    "w-5 h-5",
                                    call.status === 'active' ? 'text-emerald-500' : 'text-muted-foreground'
                                  )} />
                                </div>
                                <div className="text-left">
                                  <p className="font-medium">{call.agent_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {formatTime(call.started_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <Badge variant={call.status === 'active' ? 'default' : 'secondary'}>
                                    {call.status === 'active' ? 'Active' : 'Completed'}
                                  </Badge>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {formatDuration(call.duration_seconds)}
                                  </p>
                                </div>
                                {expandedCall === call.id ? (
                                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                )}
                              </div>
                            </Button>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <div className="px-4 pb-4 border-t border-border/50">
                              <p className="text-sm font-medium text-muted-foreground py-3">
                                Transcript ({call.transcript?.length || 0} messages)
                              </p>
                              {call.transcript && call.transcript.length > 0 ? (
                                <ScrollArea className="h-64">
                                  <div className="space-y-3">
                                    {call.transcript.map((msg, idx) => (
                                      <div 
                                        key={idx}
                                        className={cn(
                                          "p-3 rounded-lg",
                                          msg.role === "user" 
                                            ? "bg-primary/10 ml-8" 
                                            : "bg-card mr-8"
                                        )}
                                        data-testid={`transcript-${call.id}-${idx}`}
                                      >
                                        <p className="text-xs text-muted-foreground mb-1 font-medium">
                                          {msg.role === "user" ? "You" : call.agent_name}
                                        </p>
                                        <p className="text-sm">{msg.content}</p>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollArea>
                              ) : (
                                <p className="text-sm text-muted-foreground italic">
                                  No transcript available
                                </p>
                              )}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
